import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getPublicMostLikedTours, getPublicOngoingTours } from '../../../utils/Vanlifer/tour';
import { TourOverview } from '../../../services/restserver-openapi';
import { mapErrorMessages } from '../../../utils/errorHandling';
import { asyncToggleTourLike } from '../tourSlice';

type DiscoverToursState = {
    loading: boolean;
    error: string | null;
    mostLikedTours?: TourOverview[] | null;
    ongoingTours?: TourOverview[] | null;
};

const initialState: DiscoverToursState = {
    loading: false,
    error: null,
    mostLikedTours: null,
    ongoingTours: null,
};

export const asyncGetPublicMostLikedTours = createAsyncThunk(
    'discoverTours/getPublicMostLikedTours',
    async () => {
        return getPublicMostLikedTours();
    }
);

export const asyncGetPublicOngoingTours = createAsyncThunk(
    'discoverTours/getPublicOngoingTours',
    async () => {
        return getPublicOngoingTours();
    }
);

export const discoverToursSlice = createSlice({
    name: 'discoverTours',
    initialState,
    reducers: {
        resetDiscoverToursState: (state) => {
            state.loading = false;
            state.error = null;
            state.mostLikedTours = undefined;
            state.ongoingTours = undefined;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(asyncGetPublicMostLikedTours.pending, (state) => {
                state.loading = true;
            })
            .addCase(asyncGetPublicMostLikedTours.fulfilled, (state, { payload }) => {
                state.mostLikedTours = payload;
                state.loading = false;
                state.error = null;
            })
            .addCase(asyncGetPublicMostLikedTours.rejected, (state, action) => {
                state.loading = false;
                if (action.error.message) {
                    state.error = mapErrorMessages({ errorMessage: action.error.message });
                }
            })
            .addCase(asyncGetPublicOngoingTours.pending, (state) => {
                state.loading = true;
            })
            .addCase(asyncGetPublicOngoingTours.fulfilled, (state, { payload }) => {
                state.ongoingTours = payload;
                state.loading = false;
                state.error = null;
            })
            .addCase(asyncGetPublicOngoingTours.rejected, (state, action) => {
                state.loading = false;
                if (action.error.message) {
                    state.error = mapErrorMessages({ errorMessage: action.error.message });
                }
            })
            .addCase(asyncToggleTourLike.pending, (state, { meta }) => {
                const { tourId, isLiked } = meta.arg;

                const updateTour = (tour: TourOverview) => {
                    if (tour.tourId === tourId) {
                        if (typeof tour.liked === 'undefined') {
                            tour.liked = isLiked;
                            tour.likeCount = isLiked ? 1 : 0;
                        } else if (tour.liked !== isLiked) {
                            tour.liked = isLiked;
                            tour.likeCount = (tour.likeCount || 0) + (isLiked ? 1 : -1);
                        }
                    }
                };

                state.mostLikedTours?.forEach(updateTour);
                state.ongoingTours?.forEach(updateTour);
            })
            .addCase(asyncToggleTourLike.rejected, (state, { meta }) => {
                const { tourId, isLiked } = meta.arg;

                const revertTour = (tour: TourOverview) => {
                    if (tour.tourId === tourId) {
                        tour.liked = !isLiked;
                        tour.likeCount = (tour.likeCount || 0) + (isLiked ? -1 : 1);
                    }
                };

                state.mostLikedTours?.forEach(revertTour);
                state.ongoingTours?.forEach(revertTour);
            });
    },
});

export const { resetDiscoverToursState } = discoverToursSlice.actions;
export const discoverToursReducer = discoverToursSlice.reducer;
